import { Filter, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '../../lib/utils';

export function IntelligentFilterBar({
  dateFilter,
  typeFilter,
  statusFilter,
  updateFilter,
  hasActiveFilters,
  resetFilters,
  filteredBookingsCount,
  totalBookingsCount,
  uniqueSpaceNames,
}: {
  dateFilter: string;
  typeFilter: string;
  statusFilter: string;
  updateFilter: (key: string, value: string) => void;
  hasActiveFilters: boolean;
  resetFilters: () => void;
  filteredBookingsCount: number;
  totalBookingsCount: number;
  uniqueSpaceNames: string[];
}) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-3 flex flex-col md:flex-row gap-4 items-center w-full justify-between">
      <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
        <div className="flex items-center gap-2 text-slate-400 border-r border-slate-200 pr-3 mr-1">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-semibold text-slate-700 hidden sm:inline-block">Filtres</span>
        </div>

        <Select value={dateFilter} onValueChange={(val) => updateFilter('date', val)}>
          <SelectTrigger
            className={cn(
              'min-w-0 flex-1 bg-white h-auto py-1.5 focus:ring-primary/20',
              dateFilter !== 'all'
                ? 'border-primary bg-primary/5 text-primary font-medium'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            <SelectValue placeholder="Toutes Périodes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes Périodes</SelectItem>
            <SelectItem value="today">Aujourd'hui</SelectItem>
            <SelectItem value="7days">7 derniers jours</SelectItem>
            <SelectItem value="month">Ce mois-ci</SelectItem>
            <SelectItem value="year">Cette année</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={(val) => updateFilter('type', val)}>
          <SelectTrigger
            className={cn(
              'min-w-0 flex-1 bg-white h-auto py-1.5 focus:ring-primary/20',
              typeFilter !== 'all'
                ? 'border-primary bg-primary/5 text-primary font-medium'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            <SelectValue placeholder="Tous les espaces" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les espaces</SelectItem>
            {uniqueSpaceNames.map(name => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(val) => updateFilter('status', val)}>
          <SelectTrigger
            className={cn(
              'min-w-0 flex-1 bg-white h-auto py-1.5 focus:ring-primary/20',
              statusFilter !== 'all'
                ? 'border-primary bg-primary/5 text-primary font-medium'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            <SelectValue placeholder="Tous Statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous Statuts</SelectItem>
            <SelectItem value="PAID">Payé</SelectItem>
            <SelectItem value="PENDING">En attente / Confirmé</SelectItem>
            <SelectItem value="QUOTE_REQUESTED">Devis</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1.5 px-2 py-1 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Réinitialiser
          </button>
        )}
      </div>

      <div className="flex-shrink-0 whitespace-nowrap text-sm text-slate-500 font-medium ml-auto">
        {filteredBookingsCount === totalBookingsCount
          ? `${totalBookingsCount} résultat${totalBookingsCount > 1 ? 's' : ''}`
          : `${filteredBookingsCount} sur ${totalBookingsCount} résultat${totalBookingsCount > 1 ? 's' : ''}`}
      </div>
    </div>
  );
}
